import { useEffect, useRef, useState } from 'react';
import {
  canUseWebrtc, renewDelayMs, shouldRetry, RETRY_MS, type FeedWebrtcCreds,
} from './feedWebrtc';
import type { AuthHeadersFn } from '../assistant/assistantTypes';

// Defined with the rest of the pure policy, re-exported here so the existing
// import site does not have to care which of the pair it comes from.
export type { FeedWebrtcCreds };

/**
 * A subscribe-only LiveKit token for ONE feed, kept alive while the tile is open.
 *
 * Returns null whenever WebRTC is not available for this feed — no LiveKit in this
 * deployment, a browser-decoded source, the publisher budget is full, or the request
 * simply failed. **Null means "use MJPEG", never "show nothing".** That fallback is
 * what makes this safe: a tile is never worse off than before this existed, and a
 * deployment without LiveKit behaves exactly as it always did.
 *
 * Asking for the token is also what tells the backend this feed is being watched —
 * it keeps a TTL lease rather than a register/unregister pair, because a tab that
 * crashes or sleeps never sends the unregister and a publisher outliving its only
 * viewer is an H.264 encode nobody receives. So the renewal below is not just token
 * hygiene; stop renewing and the publisher is correctly torn down.
 */
export function useFeedWebrtc(
  apiUrl: string,
  authHeaders: AuthHeadersFn,
  enabled: boolean,
  sourceType?: string,
  sourceUrl?: string,
  deviceSn?: string,
): FeedWebrtcCreds | null {
  const [creds, setCreds] = useState<FeedWebrtcCreds | null>(null);
  // Kept in a ref so changing headers (a token refresh elsewhere) never restarts
  // the lease — a restart renegotiates ICE and blanks the tile for a second.
  const headersRef = useRef(authHeaders);
  headersRef.current = authHeaders;

  const active = enabled && canUseWebrtc(sourceType, deviceSn);

  useEffect(() => {
    if (!active) {
      setCreds(null);
      return;
    }
    let mounted = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    // A serial and a URL are alternatives, not companions: the backend resolves
    // the serial to the reader already pulling that aircraft and ignores the URL,
    // which for a DJI tile is the `.m3u8` the browser plays.
    const qs = deviceSn
      ? `source=${encodeURIComponent(sourceType || '')}&device_sn=${encodeURIComponent(deviceSn)}`
      : `source=${encodeURIComponent(sourceType || '')}` +
        (sourceUrl ? `&url=${encodeURIComponent(sourceUrl)}` : '');
    const endpoint = `${apiUrl}/api/drone/feed-webrtc-token?${qs}`;

    const lease = async () => {
      try {
        const res = await fetch(endpoint, { headers: headersRef.current() });
        if (!mounted) return;
        if (!res.ok) {
          setCreds(null);                       // fall back to MJPEG
          // Retry fast, not on the renewal clock: the first request normally
          // 503s because the lease it just took is what starts this feed's
          // compositor, and the publisher connects only once a frame exists.
          if (shouldRetry(res.status)) timer = setTimeout(lease, RETRY_MS);
          return;
        }
        const data = await res.json();
        if (!mounted) return;
        setCreds({ livekitUrl: data.livekit_url, token: data.token, room: data.room });
        timer = setTimeout(lease, renewDelayMs(data.renew_in_s));
      } catch {
        if (!mounted) return;
        setCreds(null);
        timer = setTimeout(lease, RETRY_MS);
      }
    };
    lease();

    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
      // Best-effort fast release. The server's TTL covers the case where this
      // never arrives — a closed laptop, a killed tab — so it is an optimisation
      // rather than something correctness depends on.
      fetch(endpoint, { method: 'DELETE', headers: headersRef.current() }).catch(() => {});
    };
  }, [active, apiUrl, sourceType, sourceUrl, deviceSn]);

  return creds;
}
